import {
  addAllValuesToField,
  addFieldsToGroup,
  addSelectedField,
  addSelectedFilter,
  addValuesToGroup,
  addValueToTree,
  changeConditionalType,
  clearSelectedFields,
  clearSelectedFilters,
  removeFieldFromTree,
  removeFieldsFromGroup,
  removeSelectedField,
  removeSelectedFilter,
  removeValueFromTree,
  removeValuesFromGroup,
} from 'actions/dashboard';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  getFiltersSearchString,
  getNavParamsFromDashboardTree,
  getSelectedFields,
  getSelectedFilters,
  getTree,
} from 'selectors/dashboard';
import { useSelector } from 'store';
import { Condition, DataNode, Operator, ValueNode } from 'utils/parserTree';
import { v4 } from 'uuid';
import { FiltersTreeContext } from './ExploreFiltersTreeContext';

const DashboardFiltersTreeContext = React.createContext<FiltersTreeContext>({
  tree: {
    op: Operator.AND,
    data: [],
    id: v4(),
  },
  treeParams: {},
  filtersSearchString: '',
  selectedValues: [],
  selectedFields: [],

  handleAddValueToTree: () => {
    // do nothing default
  },
  handleReplaceValueInTree: () => {
    // do nothing default
  },
  handleRemoveValueFromTree: () => {
    // do nothing default
  },
  handleAddAllValuesToField: () => {
    // do nothing default
  },
  handleRemoveFieldFromTree: () => {
    // do nothing default
  },
  handleChangeConditionalType: () => {
    // do nothing default
  },
  handleAddSelectedFilter: () => {
    // do nothing default
  },
  handleRemoveSelectedFilter: () => {
    // do nothing default
  },
  handleAddSelectedField: () => {
    // do nothing default
  },
  handleRemoveSelectedField: () => {
    // do nothing default
  },
  handleClearSelectedFields: () => {
    // do nothing default
  },
  handleClearSelectedFilters: () => {
    // do nothing default
  },
  handleAddValuesToGroup: () => {
    // do nothing default
  },
  handleRemoveValuesFromGroup: () => {
    // do nothing default
  },
  handleAddFieldsToGroup: () => {
    // do nothing default
  },
  handleRemoveFieldsFromGroup: () => {
    // do nothing default
  },
});

export const DashboardFiltersTreeProvider: React.FC = ({ children }) => {
  const dispatch = useDispatch();

  const tree = useSelector(getTree);
  const treeParams = useSelector(getNavParamsFromDashboardTree);
  const filtersSearchString = useSelector(getFiltersSearchString);
  const selectedFields = useSelector(getSelectedFields);
  const selectedValues = useSelector(getSelectedFilters);

  const handleAddValueToTree = (
    value: string,
    field: string,
    label: string,
    isSingleValue = false
  ): void => {
    dispatch(
      addValueToTree({
        value,
        field,
        label,
        isSingleValue,
      })
    );
  };

  const handleReplaceValueInTree = (): void => {
    // moot
  };

  const handleRemoveValueFromTree = (value: string, field?: string, fieldId?: string): void => {
    dispatch(removeValueFromTree({ value, field, fieldId }));
  };

  const handleAddAllValuesToField = (
    field: string,
    nodes: { value: string; label?: string }[]
  ): void => {
    dispatch(
      addAllValuesToField({
        field,
        nodes,
      })
    );
  };

  const handleRemoveFieldFromTree = (field: string, id?: string): void => {
    dispatch(removeFieldFromTree({ field, id }));
  };

  const handleChangeConditionalType = (node: DataNode, condition: Condition): void => {
    dispatch(changeConditionalType({ node, condition }));
  };

  const handleAddSelectedFilter = (node: ValueNode): void => {
    dispatch(addSelectedFilter({ node }));
  };

  const handleRemoveSelectedFilter = (node: ValueNode): void => {
    dispatch(removeSelectedFilter({ node }));
  };

  const handleAddSelectedField = (node: DataNode): void => {
    dispatch(addSelectedField({ node }));
  };

  const handleRemoveSelectedField = (node: DataNode): void => {
    dispatch(removeSelectedField({ node }));
  };

  const handleClearSelectedFields = (): void => {
    dispatch(clearSelectedFields());
  };

  const handleClearSelectedFilters = (): void => {
    dispatch(clearSelectedFilters());
  };
  const handleAddValuesToGroup = (nodes: ValueNode[]): void => {
    dispatch(addValuesToGroup({ nodes }));
  };

  const handleRemoveValuesFromGroup = (nodes: ValueNode[]): void => {
    dispatch(removeValuesFromGroup({ nodes }));
  };

  const handleAddFieldsToGroup = (nodes: DataNode[]): void => {
    dispatch(addFieldsToGroup({ nodes }));
  };

  const handleRemoveFieldsFromGroup = (nodes: DataNode[]): void => {
    dispatch(removeFieldsFromGroup({ nodes }));
  };

  const value = {
    tree,
    treeParams,
    filtersSearchString,
    selectedFields,
    selectedValues,

    handleAddValueToTree,
    handleReplaceValueInTree,
    handleRemoveValueFromTree,
    handleAddAllValuesToField,
    handleRemoveFieldFromTree,
    handleChangeConditionalType,
    handleAddSelectedFilter,
    handleRemoveSelectedFilter,
    handleAddSelectedField,
    handleRemoveSelectedField,
    handleClearSelectedFields,
    handleClearSelectedFilters,

    handleAddValuesToGroup,
    handleRemoveValuesFromGroup,
    handleAddFieldsToGroup,
    handleRemoveFieldsFromGroup,
  };

  return (
    <DashboardFiltersTreeContext.Provider value={value}>
      {children}
    </DashboardFiltersTreeContext.Provider>
  );
};

export default DashboardFiltersTreeContext;
